import React from "react";
import { Column, Row, Tab, TabGroup } from "@amzn/meridian";
import { Evidence } from "src/services/inspectionService";
import "./EvidenceTabs.scss";

export type TabKey = "overridden" | "original";

export interface EvidenceTabsProps {
    activeTab: TabKey;
    onTabChange: (tab: TabKey) => void;
    overriddenEvidences: Evidence[];
    originalEvidences: Evidence[];
    presignedUrlMap: Record<string, string>;
    onImageExpand?: (url: string) => void;
    /**
     * Label for the first tab (the current/authoritative evidence). Defaults to
     * "Overridden" for the manual-override flow; the fix-it-now flow passes
     * "Fix Evidence". The tab's internal value stays "overridden" either way.
     */
    firstTabLabel?: string;
    /** Label for the second tab (the AI's original evidence). Defaults to "Original result". */
    secondTabLabel?: string;
}

const EvidenceTabs: React.FC<EvidenceTabsProps> = ({
    activeTab,
    onTabChange,
    overriddenEvidences,
    originalEvidences,
    presignedUrlMap,
    onImageExpand,
    firstTabLabel = "Overridden",
    secondTabLabel = "Original result",
}) => {
    const resolveUrls = (evidences: Evidence[]): string[] =>
        evidences
            .map((e) => presignedUrlMap[e.uri])
            .filter(Boolean);

    const overriddenUrls = resolveUrls(overriddenEvidences);
    const originalUrls = resolveUrls(originalEvidences);

    const currentUrls = activeTab === "overridden" ? overriddenUrls : originalUrls;

    return (
        <Column spacing="300" className="evidence-tabs">
            <TabGroup value={activeTab} onChange={onTabChange} fill="tabs">
                <Tab value="overridden">{firstTabLabel}</Tab>
                <Tab value="original">{secondTabLabel}</Tab>
            </TabGroup>

            {currentUrls.length > 0 && (
                <Row spacing="300" wrap="down">
                    {currentUrls.map((url, idx) => (
                        <button
                            key={`${activeTab}-${idx}`}
                            type="button"
                            className="evidence-tabs__image-button"
                            onClick={() => onImageExpand?.(url)}
                            aria-label={`Expand evidence image ${idx + 1}`}
                        >
                            <img
                                src={url}
                                alt={`Evidence ${idx + 1}`}
                                className="evidence-tabs__image"
                            />
                        </button>
                    ))}
                </Row>
            )}
        </Column>
    );
};

export default EvidenceTabs;
